import { supabase } from '../lib/supabase';

// Notification types
export const NOTIFICATION_TYPES = {
  ROUND_INVITATION: 'round_invitation',
  ROUND_RESPONSE: 'round_response',
  GROUP_MEMBER_JOINED: 'group_member_joined',
  GROUP_MEMBER_LEFT: 'group_member_left',
  ROUND_REMINDER: 'round_reminder',
  AVAILABILITY_REQUEST: 'availability_request',
  AVAILABILITY_PROVIDED: 'availability_provided',
  GROUP_UPDATE: 'group_update',
  ROUND_CREATED: 'round_created',
  ROUND_CANCELLED: 'round_cancelled',
  WEATHER_SCORE_DECREASED: 'weather_score_decreased',
  FRIEND_REQUEST_RECEIVED: 'friend_request_received',
  FRIEND_REQUEST_ACCEPTED: 'friend_request_accepted',
  ROUND_JOIN_REQUEST: 'round_join_request',
  ROUND_OPEN_INVITE_CREATED: 'round_open_invite_created'
};

// Notification priorities
export const NOTIFICATION_PRIORITIES = {
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low'
};

// Create a new notification
export const createNotification = async ({
  userId,
  type,
  title,
  message,
  data = {},
  priority = NOTIFICATION_PRIORITIES.MEDIUM,
  roundId = null,
  groupId = null,
  senderId = null,
  expiresAt = null
}) => {
  try {
    const { data: notification, error } = await supabase
      .from('notifications')
      .insert([{
        user_id: userId,
        type,
        title,
        message,
        data,
        priority,
        round_id: roundId,
        group_id: groupId,
        sender_id: senderId,
        expires_at: expiresAt
      }])
      .select()
      .single();

    if (error) {
      console.error('Error creating notification:', error);
      return { data: null, error };
    }

    return { data: notification, error: null };
  } catch (error) {
    console.error('Exception in createNotification:', error);
    return { data: null, error };
  }
};

// Get notifications for a user with filtering and pagination
export const getUserNotifications = async ({
  userId = null,
  type = null,
  read = null,
  dismissed = false,
  limit = 50,
  offset = 0,
  orderBy = 'created_at',
  orderDirection = 'desc'
} = {}) => {
  try {
    // If no userId provided, get current user
    if (!userId) {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { data: [], error: 'User not authenticated' };
      }
      userId = user.id;
    }

    let query = supabase
      .from('notifications')
      .select(`
        *,
        rounds (
          id,
          date,
          time,
          course,
          golf_groups!rounds_group_id_fkey (
            id,
            name
          )
        ),
        golf_groups (
          id,
          name
        )
      `)
      .eq('user_id', userId)
      .eq('dismissed', dismissed);

    // Apply filters
    if (type) {
      query = query.eq('type', type);
    }

    if (read !== null) {
      query = query.eq('read', read);
    }

    // Filter out expired notifications
    query = query.or('expires_at.is.null,expires_at.gt.' + new Date().toISOString());

    // Apply ordering and pagination
    query = query
      .order(orderBy, { ascending: orderDirection === 'asc' })
      .range(offset, offset + limit - 1);

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching notifications:', error);
      return { data: [], error };
    }

    return { data: data || [], error: null };
  } catch (error) {
    console.error('Exception in getUserNotifications:', error);
    return { data: [], error };
  }
};

// Get unread notification count for a user
export const getUnreadNotificationCount = async (userId = null) => {
  try {
    // If no userId provided, get current user
    if (!userId) {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { data: 0, error: 'User not authenticated' };
      }
      userId = user.id;
    }

    const { data, error } = await supabase
      .rpc('get_unread_notification_count');

    if (error) {
      console.error('Error fetching unread count:', error);
      return { data: 0, error };
    }

    return { data: data || 0, error: null };
  } catch (error) {
    console.error('Exception in getUnreadNotificationCount:', error);
    return { data: 0, error };
  }
};

// Mark a notification as read
export const markNotificationAsRead = async (notificationId) => {
  try {
    const { data, error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', notificationId)
      .select()
      .single();

    if (error) {
      console.error('Error marking notification as read:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Exception in markNotificationAsRead:', error);
    return { data: null, error };
  }
};

// Mark a notification as unread
export const markNotificationAsUnread = async (notificationId) => {
  try {
    const { data, error } = await supabase
      .from('notifications')
      .update({ read: false })
      .eq('id', notificationId)
      .select()
      .single();

    if (error) {
      console.error('Error marking notification as unread:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Exception in markNotificationAsUnread:', error);
    return { data: null, error };
  }
};

// Mark multiple notifications as read
export const markNotificationsAsRead = async (notificationIds) => {
  try {
    const { data, error } = await supabase
      .from('notifications')
      .update({ read: true })
      .in('id', notificationIds)
      .select();

    if (error) {
      console.error('Error marking notifications as read:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Exception in markNotificationsAsRead:', error);
    return { data: null, error };
  }
};

// Mark all notifications as read for a user
export const markAllNotificationsAsRead = async (userId = null) => {
  try {
    // If no userId provided, get current user
    if (!userId) {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { data: null, error: 'User not authenticated' };
      }
      userId = user.id;
    }

    const { data, error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('user_id', userId)
      .eq('read', false)
      .select();

    if (error) {
      console.error('Error marking all notifications as read:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Exception in markAllNotificationsAsRead:', error);
    return { data: null, error };
  }
};

// Dismiss a notification
export const dismissNotification = async (notificationId) => {
  try {
    const { data, error } = await supabase
      .from('notifications')
      .update({ dismissed: true })
      .eq('id', notificationId)
      .select()
      .single();

    if (error) {
      console.error('Error dismissing notification:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Exception in dismissNotification:', error);
    return { data: null, error };
  }
};

// Delete a notification
export const deleteNotification = async (notificationId) => {
  try {
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', notificationId);

    if (error) {
      console.error('Error deleting notification:', error);
      return { data: null, error };
    }

    return { data: true, error: null };
  } catch (error) {
    console.error('Exception in deleteNotification:', error);
    return { data: null, error };
  }
};

// Delete all notifications of a specific type for a user
export const deleteNotificationsByType = async (type, userId = null) => {
  try {
    // If no userId provided, get current user
    if (!userId) {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { data: null, error: 'User not authenticated' };
      }
      userId = user.id;
    }

    const { data, error } = await supabase
      .from('notifications')
      .delete()
      .eq('user_id', userId)
      .eq('type', type);

    if (error) {
      console.error(`Error deleting notifications of type ${type}:`, error);
      return { data: null, error };
    }

    return { data: true, error: null };
  } catch (error) {
    console.error('Exception in deleteNotificationsByType:', error);
    return { data: null, error };
  }
};

// Clean up expired notifications
export const cleanupExpiredNotifications = async () => {
  try {
    const { data, error } = await supabase
      .rpc('cleanup_expired_notifications');

    if (error) {
      console.error('Error cleaning up expired notifications:', error);
      return { data: 0, error };
    }

    return { data: data || 0, error: null };
  } catch (error) {
    console.error('Exception in cleanupExpiredNotifications:', error);
    return { data: 0, error };
  }
};

